import React, { useState, useEffect, useRef } from "react";
import { Link, Router, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import {
  CalendarDays,
  Home,
  Star,
  Menu,
  X,
  MessageSquare,
  ShieldCheck,
  Mail,
  Calendar,
  MessageCircle,
  Shield,
  Clock,
  Instagram,
  Linkedin,
  Youtube,
  MapPin,
  Phone,
  Send,
  ChevronUp,
  ExternalLink,
  Heart,
  Globe,
  Users,
  Zap,
  AtSign,
  Eye,
  EyeOff,
  User,
  Lock,
  Sparkles,
  Plane,
  Map,
  Compass,
} from "lucide-react";
import { features, heroImages } from "../data/mock";
import { FaUserCircle } from "react-icons/fa";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import LoginPage from "./LoginSignUp/Login";
import HeroDashboardPreview from "./HeroDashboardPreview";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../LandingPage.css";
import vikram from "./Dashboard/vikram.jpg";
import Loader from "./Loader"; // Adjust the path
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getAuthHeader,
  getStoredToken,
  storeToken,
  storeUserMetadata,
} from "../lib/auth";

const heroFormSchema = z.object({
  name: z.string().min(1, "Name is required").optional(),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const LandingPage = () => {
  const navigate = useNavigate();

  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Hero inline form state
  const [heroIsSignup, setHeroIsSignup] = useState(false);
  const [heroShowPassword, setHeroShowPassword] = useState(false);
  const [heroFormLoading, setHeroFormLoading] = useState(false);
  const [heroFormError, setHeroFormError] = useState("");
  const [heroCardMounted, setHeroCardMounted] = useState(false);
  const heroLoginCardRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

  const {
    register: heroRegister,
    handleSubmit: heroHandleSubmit,
    formState: { errors: heroErrors, isSubmitting: heroIsSubmitting },
    reset: heroReset,
  } = useForm({
    resolver: zodResolver(heroFormSchema),
  });

  const onHeroFormSubmit = async (data) => {
    setHeroFormError("");
    setHeroFormLoading(true);
    if (heroIsSignup && !data.name) {
      setHeroFormError("Name is required for signup");
      setHeroFormLoading(false);
      return;
    }
    try {
      const url = heroIsSignup
        ? `${API_BASE}/api/auth/register`
        : `${API_BASE}/api/auth/login`;
      const res = await axios.post(url, data);
      const token = res?.data?.token;
      if (token) {
        storeToken(token);
        storeUserMetadata(res?.data?.user);
        axios.defaults.headers.common["Authorization"] = getAuthHeader();
        navigate("/dashboard");
      } else {
        if (res?.status === 201 || res?.status === 200) {
          setHeroFormError(
            heroIsSignup
              ? "Registered! Please log in."
              : "Login succeeded but no token returned.",
          );
        } else {
          setHeroFormError("Authentication failed.");
        }
      }
    } catch (err) {
      setHeroFormError(
        err?.response?.data?.message || err?.message || "Server error",
      );
    } finally {
      setHeroFormLoading(false);
    }
  };

  // Refs for animation targets
  const heroSectionRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroSubtitleRef = useRef(null);
  const heroButtonsRef = useRef(null);
  const heroImagesRef = useRef(null);
  const whySectionRef = useRef(null);
  const featuresRef = useRef(null);

  const journeySectionRef = useRef(null);
  const teamSectionRef = useRef(null);
  const airplaneRef = useRef(null);
  const navItemsRef = useRef([]);
  const footerRef = useRef(null);
  const journeyCardsRef = useRef([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Trigger hero card mount animation
  useEffect(() => {
    const timer = setTimeout(() => setHeroCardMounted(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    setSubscribed(true);

    setTimeout(() => {
      setSubscribed(false);
      setEmail("");
    }, 3000);
  };

  // const handleSubscribe = (e) => {
  //   e.preventDefault();
  //   if (email) {
  //     setSubscribed(true);
  //     setTimeout(() => {
  //       setSubscribed(false);
  //       setEmail('');
  //     }, 3000);
  //   }
  // };
  const getAuthTargetPath = () => (getStoredToken() ? "/dashboard" : "/login");

  const navigateToAuthTarget = ({
    showLoginModal = false,
    closeMobileMenu = false,
  } = {}) => {
    if (closeMobileMenu) {
      setIsMobileMenuOpen(false);
    }

    const target = getAuthTargetPath();

    if (target === "/login" && showLoginModal) {
      setShowLoginDialog(true);
    }

    navigate(target);
  };

  const handleExploreDashboard = () => {
    navigateToAuthTarget();
  };

  const footerSections = [
    {
      title: "Product",
      links: [
        { name: "Features", href: "#features", icon: <Zap size={14} /> },
        { name: "How It Works", href: "#working", icon: <Globe size={14} /> },
        { name: "Pricing", href: "#pricing", icon: <Heart size={14} /> },
        {
          name: "Integrations",
          href: "#integrations",
          icon: <Users size={14} />,
        },
      ],
    },
    {
      title: "Support",
      links: [
        { name: "Help Center", href: "#help" },
        { name: "Contact Us", href: "/contact" },

        { name: "Community", href: "#community" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "#about" },
        { name: "Careers", href: "#careers" },
        { name: "Blog", href: "#blog" },
        { name: "Press Kit", href: "#press" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Terms of Service", href: "/terms-and-services" },
        { name: "Cookie Policy", href: "/terms-and-services#cookies" },
        { name: "GDPR", href: "/terms-and-services#gdpr" },
      ],
    },
  ];

  const socialLinks = [
    {
      icon: <AtSign size={20} />,
      label: "Threads",
      href: "#",
      color: "hover:text-gray-300",
      bgHover: "hover:bg-gray-700",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.6l-5.165-6.75-5.913 6.75h-3.308l7.73-8.835L.424 2.25h6.74l4.888 6.469L17.244 2.25h.75zm-1.161 17.52h1.833L7.084 4.126H5.117l12.926 15.644z" />
        </svg>
      ),
      label: "X (Twitter)",
      href: "#",
      color: "hover:text-white",
      bgHover: "hover:bg-gray-600",
    },
    {
      icon: <Instagram size={20} />,
      label: "Instagram",
      href: "https://www.instagram.com/syncandexplore?igsh=MXV5OXgzdndoMjlwOQ==",
      color: "hover:text-pink-400",
      bgHover: "hover:bg-pink-900/30",
    },
    {
      icon: <Linkedin size={20} />,
      label: "LinkedIn",
      href: "https://www.linkedin.com/company/sync-explore/posts/?feedView=all",
      color: "hover:text-blue-400",
      bgHover: "hover:bg-blue-900/30",
    },
    {
      icon: <Youtube size={20} />,
      label: "YouTube",
      href: "#",
      color: "hover:text-red-400",
      bgHover: "hover:bg-red-900/30",
    },
  ];

  const contactInfo = [
    {
      icon: <MapPin size={16} />,
      text: "Hno-337g/f Kh No. 126, Saini Mohalla, Nangloi - Iii, West Delhi, New Delhi, Delhi, India, 110041",
    },
    { icon: <Phone size={16} />, text: "+91 6391433858" },
    { icon: <Mail size={16} />, text: "contact@syncandexplore.com" },
  ];

  // Add refs to arrays
  const addToNavItemsRef = (el) => {
    if (el && !navItemsRef.current.includes(el)) {
      navItemsRef.current.push(el);
    }
  };

  const addToJourneyCardsRef = (el) => {
    if (el && !journeyCardsRef.current.includes(el)) {
      journeyCardsRef.current.push(el);
    }
  };

  // Hover animation functions
  const handleCardHover = (element) => {
    gsap.to(element, {
      scale: 1.05,
      y: -5,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const handleCardLeave = (element) => {
    gsap.to(element, {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const handleNavHover = (element) => {
    gsap.to(element, {
      scale: 1.1,
      y: -2,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  const handleNavLeave = (element) => {
    gsap.to(element, {
      scale: 1,
      y: 0,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  useEffect(() => {
    // Navigation items animation
    gsap.fromTo(
      navItemsRef.current,
      { y: -20, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        delay: 0.4,
      },
    );

    // Hero section animations with improved smoothness
    const heroTl = gsap.timeline({ defaults: { ease: "power4.out" } });
    heroTl
      .fromTo(
        heroTitleRef.current,
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2 },
      )
      .fromTo(
        heroSubtitleRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        "-=0.8",
      )
      .fromTo(
        heroButtonsRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.6",
      );

    // Login card entrance handled via CSS transition (heroCardMounted state)

    // Why section animations with ScrollTrigger
    gsap.fromTo(
      whySectionRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: whySectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      },
    );

    // Airplane animation
    gsap.fromTo(
      airplaneRef.current,
      { x: -100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: airplaneRef.current,
          start: "top 85%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      },
    );

    // Features cards animation with hover effects
    gsap.fromTo(
      ".feature-card",
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: "back.out(1.2)",
        scrollTrigger: {
          trigger: featuresRef.current,
          start: "top 75%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      },
    );

    // Journey steps animation with enhanced effects
    gsap.fromTo(
      ".journey-step-container",
      {
        y: 60,
        opacity: 0,
        scale: 0.9,
      },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.7,
        stagger: 0.2,
        ease: "back.out(1.2)",
        scrollTrigger: {
          trigger: journeySectionRef.current,
          start: "top 70%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      },
    );

    // Team section animation
    if (teamSectionRef.current) {
      gsap.fromTo(
        ".team-grid-new .team-card-new",
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: teamSectionRef.current,
            start: "top 75%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }

    // Form elements animation
    if (document.querySelector(".form")) {
      gsap.fromTo(
        ".form input, .newsletter-button",
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".form",
            start: "top 85%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }

    // Footer animation
    gsap.fromTo(
      footerRef.current,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top 90%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      },
    );

    // Footer links animation
    if (footerRef.current) {
      const footerLinks = footerRef.current.querySelectorAll("a");
      if (footerLinks.length > 0) {
        gsap.fromTo(
          footerLinks,
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: footerRef.current,
              start: "top 85%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          },
        );
      }
    }

    // Footer icons animation
    if (footerRef.current) {
      const socialIcons = footerRef.current.querySelectorAll(".group svg");
      if (socialIcons.length > 0) {
        gsap.fromTo(
          socialIcons,
          {
            scale: 0,
            rotation: -180,
          },
          {
            scale: 1,
            rotation: 0,
            duration: 0.5,
            stagger: 0.1,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: footerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          },
        );
      }
    }

    // Clean up ScrollTrigger instances
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // Animation for mobile menu
  useEffect(() => {
    if (isMobileMenuOpen) {
      gsap.fromTo(
        ".lg\\:hidden.fixed .fixed",
        { x: 300, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.3, ease: "power2.out" },
      );
    }
  }, [isMobileMenuOpen]);

  let timer;

  return (
    <div className="landing-page overflow-x-hidden">
      {/* Hero Section */}
      <section
        id="home"
        className="hero-section overflow-x-hidden"
        ref={heroSectionRef}
      >
        {/* One-time fly-across plane animation */}
        <div className="hero-plane-flyover" aria-hidden="true">
          <svg
            className="hero-plane-svg"
            viewBox="0 0 512 512"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: "rotate(15deg)" }}
          >
            <path
              d="M498.4 228l-138.4-44-112-144h-40l64 156.8-144 15.2-64-52h-32l40 76-40 76h32l64-52 144 15.2-64 156.8h40l112-144 138.4-44c7.6-2.4 7.6-13.6 0-16z"
              fill="url(#planeGrad)"
            />
            <defs>
              <linearGradient
                id="planeGrad"
                x1="0%"
                y1="50%"
                x2="100%"
                y2="50%"
              >
                <stop offset="0%" stopColor="#88E1E5" />
                <stop offset="50%" stopColor="#1A8F98" />
                <stop offset="100%" stopColor="#0b959c" />
              </linearGradient>
            </defs>
          </svg>
          <div className="hero-plane-trail"></div>
        </div>

        {/* Animated gradient orbs for visual depth */}
        <div className="hero-gradient-orbs" aria-hidden="true">
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
          <div className="hero-orb hero-orb-4"></div>
        </div>
        {/* Subtle grid mesh overlay */}
        <div className="hero-grid-mesh" aria-hidden="true"></div>
        {/* Animated particles */}
        <div className="hero-particles" aria-hidden="true">
          <div className="hero-particle hero-particle-1"></div>
          <div className="hero-particle hero-particle-2"></div>
          <div className="hero-particle hero-particle-3"></div>
          <div className="hero-particle hero-particle-4"></div>
          <div className="hero-particle hero-particle-5"></div>
          <div className="hero-particle hero-particle-6"></div>
        </div>

        <nav className="hero-nav items-center px-10 py-4 pb-8 relative z-10">
          <div className="sync flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center gap-3 no-underline">
              <img
                src="/images/sync_logo.png"
                className="w-12 lg:w-14 h-auto z-10 block object-contain relative hover:scale-110 transition-transform duration-300 cursor-pointer drop-shadow-lg"
                alt="Sync and Explore Logo"
              />
              <span className="hero-brand-name hidden sm:inline-block whitespace-nowrap">
                Sync & Explore
              </span>
            </Link>
          </div>

          {/* Hamburger Menu Button - Only visible on mobile */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden focus:outline-none p-2 hover:scale-110 transition-transform duration-200 text-gray-900 hover:text-teal-600 relative z-50"
          >
            {isMobileMenuOpen ? (
              <X className="w-8 h-8" />
            ) : (
              <Menu className="w-8 h-8" />
            )}
          </button>

          {/* Desktop Menu */}
          <ul className="nav-list lg:flex items-center gap-8 font-roboto tracking-wide">
            <li>
              <button
                onClick={() => scrollToSection("home")}
                ref={addToNavItemsRef}
                onMouseEnter={(e) => handleNavHover(e.currentTarget)}
                onMouseLeave={(e) => handleNavLeave(e.currentTarget)}
                className="block py-2 px-2 rounded-lg transition-colors duration-200 bg-none border-none cursor-pointer text-inherit"
              >
                Home
              </button>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("features")}
                ref={addToNavItemsRef}
                onMouseEnter={(e) => handleNavHover(e.currentTarget)}
                onMouseLeave={(e) => handleNavLeave(e.currentTarget)}
                className="block py-2 px-2 rounded-lg transition-colors duration-200 bg-none border-none cursor-pointer text-inherit"
              >
                Features
              </button>
            </li>
            <li>
              <Link
                to="/contact"
                ref={addToNavItemsRef}
                onMouseEnter={(e) => handleNavHover(e.currentTarget)}
                onMouseLeave={(e) => handleNavLeave(e.currentTarget)}
                className="block py-2 px-2 rounded-lg  transition-colors duration-200"
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/careers"
                ref={addToNavItemsRef}
                onMouseEnter={(e) => handleNavHover(e.currentTarget)}
                onMouseLeave={(e) => handleNavLeave(e.currentTarget)}
                className="block py-2 px-2 rounded-lg  transition-colors duration-200"
              >
                Careers
              </Link>
            </li>
            <li className="relative">
              <button
                onClick={() => {
                  setOpen(false);
                  navigateToAuthTarget({ showLoginModal: true });
                }}
                className="flex items-center justify-center p-1 text-teal-600 hover:opacity-70 transition-opacity duration-300 focus:outline-none"
                title="Login / Signup"
              >
                <FaUserCircle size={25} />
              </button>

              {/* User Dropdown Menu */}
              {open && (
                <div className="fixed right-4 top-12 mt-2 w-32 bg-white text-black rounded-lg shadow-lg overflow-visible z-[9999] animate-in fade-in-0 zoom-in-95 duration-100">
                  <button
                    className="dropdown-menu-button block w-full px-4 py-2 hover:bg-gradient-to-r hover:from-teal-50 hover:to-cyan-50 text-left transition-colors duration-200"
                    onClick={() => {
                      setOpen(false);
                      navigateToAuthTarget({ showLoginModal: true });
                    }}
                  >
                    Login
                  </button>
                  <button
                    className="dropdown-menu-button block w-full px-4 py-2 hover:bg-gradient-to-r hover:from-teal-50 hover:to-cyan-50 text-black text-left transition-colors duration-200"
                    onClick={() => {
                      setOpen(false);
                      if (getStoredToken()) {
                        navigate("/dashboard");
                        return;
                      }
                      setShowSignupDialog(true);
                      navigate("/login");
                    }}
                  >
                    Signup
                  </button>
                </div>
              )}
            </li>
          </ul>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
              <div
                className="fixed inset-0  bg-opacity-50"
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
              <div className="fixed font-roboto right-0 top-0 h-full w-64 bg-white shadow-lg mobile-menu-container">
                <div className="mobile-menu-header">
                  <h2 className="text-xl font-semibold">Menu</h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="focus:outline-none hover:scale-110 transition-transform duration-200"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="mobile-menu-content">
                  <ul className="mobile-menu-list">
                    <li className="mobile-menu-item">
                      <button
                        onClick={() => {
                          scrollToSection("home");
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full text-left py-2 text-gray-800 hover:text-teal-600 hover:translate-x-2 transition-transform duration-200 bg-none border-none cursor-pointer"
                      >
                        Home
                      </button>
                    </li>
                    <li className="mobile-menu-item">
                      <button
                        onClick={() => {
                          scrollToSection("features");
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full text-left py-2 text-gray-800 hover:text-teal-600 hover:translate-x-2 transition-transform duration-200 bg-none border-none cursor-pointer"
                      >
                        Features
                      </button>
                    </li>
                    <li className="mobile-menu-item">
                      <Link
                        to="/contact"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-2 text-gray-800 hover:text-teal-600 hover:translate-x-2 transition-transform duration-200"
                      >
                        Contact
                      </Link>
                    </li>
                    <li className="mobile-menu-item">
                      <Link
                        to="/careers"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-2 text-gray-800 hover:text-teal-600 hover:translate-x-2 transition-transform duration-200"
                      >
                        Careers
                      </Link>
                    </li>
                    <li className="mobile-menu-divider">
                      <div className="mobile-menu-button-container">
                        <button
                          onClick={() => {
                            navigateToAuthTarget({
                              showLoginModal: true,
                              closeMobileMenu: true,
                            });
                          }}
                          className="mobile-premium-btn w-full mt-4 py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                          <FaUserCircle size={16} />
                          Login
                        </button>
                        <button
                          onClick={() => {
                            if (getStoredToken()) {
                              navigateToAuthTarget({ closeMobileMenu: true });
                              return;
                            }
                            setIsMobileMenuOpen(false);
                            setShowSignupDialog(true);
                            navigate("/login");
                          }}
                          className="mobile-premium-btn w-full mt-2 py-3 px-4 border-2 border-teal-500 text-teal-600 font-semibold rounded-full hover:bg-teal-50 hover:scale-105 transition-all duration-300"
                        >
                          Sign Up
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </nav>

        <div className="hero-container hero-split-layout">
          {/* Floating background icons */}
          <div className="hero-bg-icons" aria-hidden="true">
            <div className="hero-bg-icon hero-bg-icon-1">
              <Plane size={24} />
            </div>
            <div className="hero-bg-icon hero-bg-icon-2">
              <Map size={20} />
            </div>
            <div className="hero-bg-icon hero-bg-icon-3">
              <Compass size={22} />
            </div>
            <div className="hero-bg-icon hero-bg-icon-4">
              <Globe size={18} />
            </div>
            <div className="hero-bg-icon hero-bg-icon-5">
              <MapPin size={20} />
            </div>
          </div>

          {/* LEFT COLUMN — Copy + CTAs */}
          <div className="hero-left">
            <h1 className="hero-title" ref={heroTitleRef}>
              We Sync, AI Plans,
              <br />
              You Explore
            </h1>
            <p className="hero-subtitle font-roboto" ref={heroSubtitleRef}>
              AI-powered travel buddy matching and smart itinerary planning — so
              you travel with the right people, at the right time, to the right
              places.
            </p>
            <div className="button-row" ref={heroButtonsRef}>
              <Button
                onClick={handleExploreDashboard}
                className="primary-btn hero-cta-primary font-roboto"
                onMouseEnter={(e) =>
                  gsap.to(e.currentTarget, {
                    scale: 1.06,
                    y: -2,
                    duration: 0.3,
                    ease: "power2.out",
                  })
                }
                onMouseLeave={(e) =>
                  gsap.to(e.currentTarget, {
                    scale: 1,
                    y: 0,
                    duration: 0.3,
                    ease: "power2.out",
                  })
                }
              >
                Explore Now
              </Button>
              <Button
                onClick={() => (window.location.href = "#working")}
                variant="outline"
                className="secondary-btn hero-cta-secondary font-roboto"
                onMouseEnter={(e) =>
                  gsap.to(e.currentTarget, {
                    scale: 1.06,
                    y: -2,
                    duration: 0.3,
                    ease: "power2.out",
                  })
                }
                onMouseLeave={(e) =>
                  gsap.to(e.currentTarget, {
                    scale: 1,
                    y: 0,
                    duration: 0.3,
                    ease: "power2.out",
                  })
                }
              >
                See how it works
              </Button>
            </div>
            <div className="hero-trust-strip">
              <div className="hero-trust-avatars">
                <div
                  className="hero-trust-avatar"
                  style={{ background: "#1A8F98" }}
                >
                  <Users size={14} color="#fff" />
                </div>
                <div
                  className="hero-trust-avatar"
                  style={{ background: "#0b959c" }}
                >
                  <Globe size={14} color="#fff" />
                </div>
                <div
                  className="hero-trust-avatar"
                  style={{ background: "#7ee0f3" }}
                >
                  <Heart size={14} color="#fff" />
                </div>
              </div>
              <span className="hero-trust-text">
                Trusted by modern travelers worldwide
              </span>
            </div>
          </div>

          {/* RIGHT COLUMN — Visual Dashboard Preview */}
          <div className="hero-right" ref={heroImagesRef}>
            {/* Abstract visual wave behind dashboard */}
            <div className="hero-visual-wave" aria-hidden="true">
              <div className="hero-wave-inner"></div>
            </div>
            {/* Radial glow behind dashboard */}
            <div className="hero-card-glow" aria-hidden="true"></div>

            <HeroDashboardPreview />
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section id="features" className="why-section" ref={whySectionRef}>
        <h2 className="why-title ">Why Sync and Explore?</h2>

        {/* Airplane image below the heading */}
        <div className="airplane-path mb-24" ref={airplaneRef}>
          <img
            src="/images/Vector.svg"
            alt="plane"
            className="w-50 h-50 relative z-10 mb-72 hover:scale-110 transition-transform duration-500 cursor-pointer"
            onMouseEnter={(e) =>
              gsap.to(e.currentTarget, {
                scale: 1.1,
                x: 20,
                duration: 0.5,
                ease: "power2.out",
              })
            }
            onMouseLeave={(e) =>
              gsap.to(e.currentTarget, {
                scale: 1,
                x: 0,
                duration: 0.5,
                ease: "power2.out",
              })
            }
          />
        </div>
        <div className="mb-8"></div>

        <div
          className="cards gap-6 font-roboto tracking-wide"
          ref={featuresRef}
        >
          {features.map((feature, index) => {
            const IconComponent =
              feature.icon === "CalendarDays"
                ? CalendarDays
                : feature.icon === "Globe"
                  ? Globe
                  : feature.icon === "Users"
                    ? Users
                    : Home;

            return (
              <Card
                key={feature.id}
                className="feature-card hover:shadow-xl transition-all duration-400 cursor-pointer"
                onMouseEnter={(e) => handleCardHover(e.currentTarget)}
                onMouseLeave={(e) => handleCardLeave(e.currentTarget)}
              >
                <CardContent className="card-content">
                  <h3
                    className="card-title"
                    dangerouslySetInnerHTML={{
                      __html: feature.title.replace(/\s(?=\w+$)/, "<br />"),
                    }}
                  ></h3>
                  <div className="card-icon-wrapper">
                    <Link
                      to={getAuthTargetPath()}
                      className="card-icon hover:scale-110 transition-transform duration-300"
                      onMouseEnter={(e) =>
                        gsap.to(e.currentTarget, {
                          scale: 1.1,
                          rotation: 360,
                          duration: 0.6,
                        })
                      }
                      onMouseLeave={(e) =>
                        gsap.to(e.currentTarget, {
                          scale: 1,
                          rotation: 0,
                          duration: 0.6,
                        })
                      }
                    >
                      <IconComponent className="w-6 h-6" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Journey Steps Section */}
      <section
        className="journey-section "
        id="working"
        ref={journeySectionRef}
      >
        <h2 className="journey-title">How AI Crafts Your Journey</h2>
        <div className="journey-path">
          {[
            {
              step: 1,
              title: "Share Your Preferences",
              desc: "Tell us what inspires you to travel.",
              icon: Users,
              image:
                "https://img.freepik.com/free-photo/friends-traveling-discovering-new-places_23-2149124741.jpg",
            },
            {
              step: 2,
              title: "Sync Your Calendar",
              desc: "We will find the perfect time slots.",
              icon: CalendarDays,
              image:
                "https://img.freepik.com/premium-photo/businessman-using-laptop-highlight-appointment-reminders-meeting-agenda_112554-1261.jpg",
            },
            {
              step: 3,
              title: "Review AI Recommendations",
              desc: "Get personalized destination matches",
              icon: Globe,
              image:
                "https://img.freepik.com/premium-photo/chat-with-ai-artificial-intelligence-young-businessman-chatting-with-smart-ai_184421-3840.jpg",
            },
            {
              step: 4,
              title: "Confirm & Go",
              desc: "One click and you are all set.",
              icon: Star,
              image:
                "https://img.freepik.com/premium-photo/rear-view-man-standing-mountain-forest_1048944-18833423.jpg",
            },
          ].map((journey, index) => {
            const IconComponent = journey.icon;
            const position = index % 2 === 0 ? "left" : "right";

            return (
              <div
                key={journey.step}
                className={`journey-step-container ${position}`}
              >
                <Card
                  className="journey-card hover:shadow-xl transition-all duration-400 cursor-pointer"
                  ref={addToJourneyCardsRef}
                  onMouseEnter={(e) => handleCardHover(e.currentTarget)}
                  onMouseLeave={(e) => handleCardLeave(e.currentTarget)}
                >
                  <CardContent className="journey-content">
                    <div className="step-number hover:scale-110 transition-transform duration-300">
                      {journey.step}
                    </div>
                    <div
                      className="step-image hover:scale-105 transition-transform duration-500"
                      onMouseEnter={(e) =>
                        gsap.to(e.currentTarget, { scale: 1.05, duration: 0.5 })
                      }
                      onMouseLeave={(e) =>
                        gsap.to(e.currentTarget, { scale: 1, duration: 0.5 })
                      }
                    >
                      <img src={journey.image} alt={journey.title} />
                    </div>
                    <h3 className="hover:text-teal-600 transition-colors duration-200">
                      {journey.title}
                    </h3>
                    <p className="hover:text-gray-700 transition-colors duration-200">
                      {journey.desc}
                    </p>
                    <Link
                      to={getAuthTargetPath()}
                      className="step-icon hover:scale-110 transition-transform duration-300"
                      onMouseEnter={(e) =>
                        gsap.to(e.currentTarget, {
                          scale: 1.1,
                          rotation: 360,
                          duration: 0.6,
                        })
                      }
                      onMouseLeave={(e) =>
                        gsap.to(e.currentTarget, {
                          scale: 1,
                          rotation: 0,
                          duration: 0.6,
                        })
                      }
                    >
                      <IconComponent className="w-8 h-8" />
                    </Link>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </section>

      {/* Our Team Section - Redesigned (Commented out) */}
      {/* 
      <section className="team-section-new" ref={teamSectionRef}>
        <div className="team-container-new">
          <h2 className="team-title-new ">Meet Our Team</h2>
          <p className="team-subtitle-new font-roboto tracking-wide">
            The passionate minds behind Sync and Explore
          </p>
          <div className="team-grid-new">
            {[
              {
                name: "Abhishek Vikram Singh",
                role: "Founder & CEO",
                image: vikram,
                bio: "Travel enthusiast with a vision to revolutionize travel planning",
                expertise: "Leadership & Strategy",
              },
              {
                name: "Anshuman Pandey",
                role: "Co-Founder & COO",
                image: "/images/anshuman.png",
                bio: "travel tech innovator with a passion for creating seamless travel experiences",
                expertise: "leadership & Strategy",
              },
              {
                name: "Kshitij Saxena",
                role: "CTO and ML Engineer",
                image: "/images/kshitij.jpg",
                bio: "AI specialist building intelligent machine learning solutions for smart travel experiences.",
                expertise: "Machine learning & AI",
              },
            ].map((member, index) => (
              <Card
                key={index}
                className="team-card-new"
                onMouseEnter={(e) => {
                  gsap.to(e.currentTarget, {
                    y: -10,
                    duration: 0.3,
                    ease: "power2.out",
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.currentTarget, {
                    y: 0,
                    duration: 0.3,
                    ease: "power2.out",
                  });
                }}
              >
                <CardContent className="team-card-content-new">
                  <div className="team-image-wrapper-new">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="team-image-new"
                    />
                    <div className="team-expertise-new">{member.expertise}</div>
                  </div>
                  <div className="team-info-new">
                    <h3 className="team-name-new">{member.name}</h3>
                    <p className="team-role-new">{member.role}</p>
                    <p className="team-bio-new">{member.bio}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Our Office Location Section */}
      <section className="office-location-section">
        <div className="office-location-container">
          <div className="office-location-header">
            <div className="office-location-icon">
              <MapPin size={28} />
            </div>
            <h2 className="office-location-title">Our Office Location</h2>
            <p className="office-location-subtitle">
              Visit our office or connect with our team for collaboration and
              support.
            </p>
          </div>

          <div className="office-location-grid">
            {/* Left Column - Google Map */}
            <div className="office-map-wrapper">
              <iframe
                title="Sync and Explore Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3500.6548!2d77.0668!3d28.6836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0499e9b0b0c1%3A0x0!2sNangloi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Right Column - Info Card */}
            <div className="office-info-card">
              <div className="office-info-card-inner">
                <h3 className="office-info-name">Sync and Explore</h3>
                <div className="office-info-address">
                  <MapPin size={18} className="office-info-icon" />
                  <div>
                    <p>Hno-337g/f Kh No. 126, Saini Mohalla</p>
                    <p>Nangloi – II, West Delhi</p>
                    <p>New Delhi, Delhi – 110041</p>
                    <p>India</p>
                  </div>
                </div>
                <div className="office-info-contact">
                  <div className="office-info-row">
                    <Phone size={18} className="office-info-icon" />
                    <a href="tel:+916391433858">+91 6391433858</a>
                  </div>
                  <div className="office-info-row">
                    <Mail size={18} className="office-info-icon" />
                    <a href="mailto:contact@syncandexplore.com">
                      contact@syncandexplore.com
                    </a>
                  </div>
                </div>
                <div className="office-action-buttons">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=Nangloi+Delhi+110041"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="office-btn office-btn-directions"
                  >
                    <MapPin size={16} />
                    Get Directions
                  </a>
                  <a
                    href="mailto:contact@syncandexplore.com"
                    className="office-btn office-btn-contact"
                  >
                    <Mail size={16} />
                    Contact Office
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-[#1A8F98] to-[#0b959c] footer text-white overflow-hidden">
        {/* Embedded Themed Background Image */}
        <div
          className="absolute inset-0 z-0 opacity-20 mix-blend-overlay transition-transform duration-1000 hover:scale-105"
          style={{
            backgroundImage: "url('/images/worldmap.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>

        {/* Gradient Overlay for added depth and readability */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/5 via-transparent to-black/30 pointer-events-none"></div>

        {/* Decorative top border */}
        <div className="absolute top-0 left-0 right-0 h-1 px-6 py-12 footer-content z-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 relative z-10">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-12">
            {/* Brand Section - Takes more space on desktop */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="sync flex justify-center md:justify-start">
                  <img
                    src="/images/sync_logo.png"
                    className="footer-logo w-16 h-auto ml-0 md:ml-6 z-10 block object-contain relative hover:scale-105 transition-transform duration-300 cursor-pointer"
                    alt="Sync and Explore Logo"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Sync and Explore
                </h2>
              </div>
              <p className="text-gray-100 mb-6 leading-relaxed ">
                We Sync, AI Plans. You Explore. Your intelligent travel
                companion for seamless adventures around the world.
              </p>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                {contactInfo.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 text-sm group"
                  >
                    <span className="text-blue-400 mt-0.5 group-hover:scale-110 transition-transform">
                      {item.icon}
                    </span>
                    <span className="text-gray-300 group-hover:text-gray-300 transition-colors">
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="flex gap-3 mt-4">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    aria-label={social.label}
                    title={social.label}
                    className={`group relative w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center ${social.color} ${social.bgHover} transition-all duration-300 hover:scale-125 hover:shadow-2xl hover:-translate-y-2 border border-slate-600 hover:border-slate-400 cursor-pointer`}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {social.icon}
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </a>
                ))}
              </div>
            </div>

            {/* Links Sections - Better spacing on mobile */}
            <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6">
              {footerSections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {section.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <a
                          href={link.href}
                          onMouseEnter={() =>
                            setHoveredLink(`${idx}-${linkIdx}`)
                          }
                          onMouseLeave={() => setHoveredLink(null)}
                          className="text-sm text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1.5 group"
                        >
                          {link.icon && (
                            <span className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                              {link.icon}
                            </span>
                          )}
                          <span
                            className={`${hoveredLink === `${idx}-${linkIdx}` ? "translate-x-1" : ""} transition-transform duration-200`}
                          >
                            {link.name}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Newsletter Section */}
            <div className="lg:col-span-3">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                Stay Updated
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Get travel tips, exclusive deals, and AI-powered
                recommendations.
              </p>
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                  <Mail
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                    size={18}
                  />
                </div>
                {emailError && (
                  <p className="text-red-400 text-sm mt-1">{emailError}</p>
                )}
                {subscribed && (
                  <p className="text-green-400 text-sm mt-1">
                    Subscribed successfully!
                  </p>
                )}
                <button
                  type="submit"
                  className="newsletter-button w-full py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 border-2 bg-white border-[#1A8F98] hover:shadow-lg hover:scale-105"
                >
                  <span className="bg-gradient-to-b from-[#1A8F98] to-[#0b959c] bg-clip-text text-transparent font-semibold">
                    Subscribe
                  </span>
                  <Send
                    size={16}
                    className="text-transparent bg-gradient-to-b from-[#1A8F98] to-[#0b959c] bg-clip-text"
                  />
                </button>
              </form>

              {/* Trust Badges */}
              <div className="mt-6 flex items-center gap-4 text-xs text-gray-300">
                <div className="flex items-center gap-1">
                  <Shield size={14} className="text-green-300" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} className="text-blue-300" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
              <p className="text-gray-100 text-center sm:text-left">
                © {new Date().getFullYear()} Sync and Explore. All rights
                reserved. Made with{" "}
                <Heart
                  size={14}
                  className="inline text-red-500 animate-pulse"
                />{" "}
                for travelers.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <a
                  href="#sitemap"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                >
                  Sitemap <ExternalLink size={12} />
                </a>
                <a
                  href="#accessibility"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Accessibility
                </a>
                <a
                  href="#cookies"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Cookie Settings
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>
      </footer>
      {/* Login Dialog */}
      <Dialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        className="w-screen h-screen"
      >
        <DialogContent className="p-0 border-none bg-transparent max-w-full h-full">
          <DialogTitle className="sr-only">Login</DialogTitle>
          <DialogDescription className="sr-only">
            Sign in to your account to manage your trips and bookings
          </DialogDescription>
          <LoginPage
            isSignup={false}
            setShowLoginDialog={setShowLoginDialog}
            setShowSignupDialog={setShowSignupDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Signup Dialog */}
      <Dialog
        open={showSignupDialog}
        onOpenChange={setShowSignupDialog}
        className="w-screen h-screen"
      >
        <DialogContent className="p-0 border-none bg-transparent max-w-full h-full">
          <DialogTitle className="sr-only">Sign Up</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new account to plan and book your trips
          </DialogDescription>
          <LoginPage
            isSignup={true}
            setShowLoginDialog={setShowLoginDialog}
            setShowSignupDialog={setShowSignupDialog}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
